/**
 * A client's card stock: the colour every one of their job cards is printed
 * on. Eight pairs from the design system (tokens.json, `stock-*`), each with
 * its own ink so text always reads. A client keeps the same stock everywhere.
 *
 * Stocks are only for job cards and a client's own marks. They never colour
 * money, status or equipment categories.
 *
 * Until clients can choose a stock in their settings, it is derived from the
 * client's name, so it is stable across pages, reloads and devices.
 */
export const CARD_STOCKS = ['tide', 'claret', 'umber', 'wheat', 'terracotta', 'sage', 'slate', 'linen'];

// FNV-1a: spreads short, similar names ("… Media", "… Productions")
// across the stocks far better than a plain multiply-by-31 hash.
function hashString(value) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** The stock name for a client, e.g. "tide". A job with no client gets linen. */
export function getClientStock(clientName) {
  const key = (clientName || '').trim().toLowerCase();
  if (!key) return 'linen';
  return CARD_STOCKS[hashString(key) % CARD_STOCKS.length];
}

/** CSS custom properties that print an element on the client's stock. */
export function clientStockStyle(clientName) {
  const stock = getClientStock(clientName);
  return {
    '--stock': `hsl(var(--stock-${stock}))`,
    '--stock-ink': `hsl(var(--stock-${stock}-ink))`,
  };
}

/** Up to two initials for a client's monogram: "Saltwater Swim Co." -> "SS". */
export function clientMonogram(clientName) {
  const words = (clientName || '').replace(/[^\p{L}\p{N}\s&]/gu, ' ').split(/\s+/).filter((w) => w && w !== '&');
  if (words.length === 0) return '–';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
